import { PRICING_MODEL } from '@/utils/pricing-model-constants';

const ADDON_ID_KEY_PATTERN = /^addons\[id\]\[(\d+)\]$/;
const SUBSCRIPTION_ITEM_PRICE_ID_PATTERN = /^subscription_items\[item_price_id\]\[(\d+)\]$/;
const ITEM_TIER_ID_KEY_PATTERN = /^item_tiers\[item_price_id\]\[(\d+)\]$/;
const TIER_BASED_PRICING_MODELS = new Set([PRICING_MODEL.TIERED, PRICING_MODEL.VOLUME, PRICING_MODEL.STAIRSTEP]);
const TIER_OVERRIDE_FIELDS = [
  'starting_unit',
  'ending_unit',
  'price',
  'starting_unit_in_decimal',
  'ending_unit_in_decimal',
  'price_in_decimal',
  'pricing_type',
  'package_size',
];

function setParamIfPresent(params, key, value) {
  if (value !== undefined && value !== null) {
    params[key] = value;
  }
}

function groupTiersByItemPriceId(itemTiers) {
  const tiersByItemPriceId = new Map();
  for (const tier of (itemTiers || [])) {
    const list = tiersByItemPriceId.get(tier.item_price_id) || [];
    list.push(tier);
    tiersByItemPriceId.set(tier.item_price_id, list);
  }
  return tiersByItemPriceId;
}

function getNextItemTierIndex(params) {
  let maxIndex = -1;
  for (const key of Object.keys(params)) {
    const match = ITEM_TIER_ID_KEY_PATTERN.exec(key);
    if (match) {
      maxIndex = Math.max(maxIndex, Number(match[1]));
    }
  }
  return maxIndex + 1;
}

function injectTierOverride(params, addonId, tiers, tierState) {
  const alreadyInjectedForAddon = Object.keys(params).some(
    k => ITEM_TIER_ID_KEY_PATTERN.test(k) && params[k] === addonId
  );
  if (alreadyInjectedForAddon) return;
  if (!tiers.length) return;

  for (const tier of tiers) {
    const i = tierState.nextIndex++;
    params[`item_tiers[item_price_id][${i}]`] = addonId;
    for (const field of TIER_OVERRIDE_FIELDS) {
      setParamIfPresent(params, `item_tiers[${field}][${i}]`, tier[field]);
    }
  }
}

function injectUnitPriceOverride(params, index, subscriptionAddon, keyPrefix = 'addons') {
  const unitPriceKey = `${keyPrefix}[unit_price][${index}]`;
  const unitPriceMdKey = `${keyPrefix}[unit_price_in_decimal][${index}]`;
  const formAlreadySetPrice =
    params[unitPriceKey] !== undefined || params[unitPriceMdKey] !== undefined;
  if (formAlreadySetPrice) return;

  setParamIfPresent(params, unitPriceKey, subscriptionAddon?.unit_price);
  setParamIfPresent(params, unitPriceMdKey, subscriptionAddon?.unit_price_in_decimal);
}

function shouldPreserveSubscriptionAddonPrice(addonId, removedAddonIds) {
  return !removedAddonIds.has(addonId);
}

function injectLegacyAddonUnitPrices(params, subscriptionAddonsById, orderAddonsById, subTiersByItemPriceId, tierState, removedAddonIds) {
  for (const key of Object.keys(params)) {
    const match = ADDON_ID_KEY_PATTERN.exec(key);
    if (!match) continue;

    const index = match[1];
    const addonId = params[key];
    if (!shouldPreserveSubscriptionAddonPrice(addonId, removedAddonIds)) continue;

    const orderAddon = orderAddonsById.get(addonId);
    if (!orderAddon) continue;
    const pricingModel = orderAddon.pricing_model || orderAddon.details?.pricing_model;

    if (TIER_BASED_PRICING_MODELS.has(pricingModel)) {
      injectTierOverride(params, addonId, subTiersByItemPriceId.get(addonId) || [], tierState);
    } else {
      injectUnitPriceOverride(params, index, subscriptionAddonsById.get(addonId));
    }
  }
}

function injectItemsModelAddonUnitPrices(params, subscriptionAddonsById, orderAddonsById, subTiersByItemPriceId, tierState, removedAddonIds) {
  for (const key of Object.keys(params)) {
    const match = SUBSCRIPTION_ITEM_PRICE_ID_PATTERN.exec(key);
    if (!match) continue;

    const index = match[1];
    const itemPriceId = params[key];
    const orderAddon = orderAddonsById.get(itemPriceId);
    if (!orderAddon) continue;
    if (!shouldPreserveSubscriptionAddonPrice(itemPriceId, removedAddonIds)) continue;

    const pricingModel = orderAddon?.pricing_model || orderAddon?.details?.pricing_model;

    if (TIER_BASED_PRICING_MODELS.has(pricingModel)) {
      injectTierOverride(params, itemPriceId, subTiersByItemPriceId.get(itemPriceId) || [], tierState);
    } else {
      injectUnitPriceOverride(params, index, subscriptionAddonsById.get(itemPriceId), 'subscription_items');
    }
  }
}

export function injectAddonUnitPrices(params, getters, rootState) {
  if (!getters.isPriceOverrideAllowed) return;

  const subscription = rootState.customer?.subscriptions
    ?.find(s => s.id === getters.subscription_handle);
  if (!subscription?.addons?.length) return;

  const subscriptionAddonsById = new Map(
    subscription.addons.map(a => [a.id, a])
  );
  const orderAddonsById = new Map(
    (getters.order_items?.addons || []).map(a => [a.id, a])
  );
  const subTiersByItemPriceId = groupTiersByItemPriceId(subscription.item_tiers);
  const removedAddonIds = new Set(getters.removed_addon_ids || []);
  const tierState = { nextIndex: getNextItemTierIndex(params) };

  injectLegacyAddonUnitPrices(params, subscriptionAddonsById, orderAddonsById, subTiersByItemPriceId, tierState, removedAddonIds);
  injectItemsModelAddonUnitPrices(params, subscriptionAddonsById, orderAddonsById, subTiersByItemPriceId, tierState, removedAddonIds);
}
